import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Grid,
  Box,
  useTheme,
  useMediaQuery,
  Alert,
  CircularProgress,
  Backdrop,
  Paper,
  Menu,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Column } from 'react-table';
import { CloudDownload, Check, Delete, Visibility, Edit, BarChart, Comment, Email, Help } from '@mui/icons-material';
import { Sidebar } from '../../common/components/Sidebar';
import { NavItem } from '../../common/components/Sidebar/Sidebar.types';
import { MainContent } from './Home.styled';
import SectionBar from '../../common/components/SectionBar/SectionBar';
import { PendingAction } from '../../types/modules';
import { useNavItems } from '../../hooks';
import { useE55Data, SortConfig as E55SortConfig, FilterConfig as E55FilterConfig } from '../e55/e55-message/hooks';
import { useE53AcceptFile, useE53Data, SortConfig as E53SortConfig, FilterConfig as E53FilterConfig } from '../e53/e53-message/hooks';
import { useENO1Data, SortConfig as ENO1SortConfig, FilterConfig as ENO1FilterConfig } from '../eNO1/eNO1-message/hooks';
import { useE09Data, SortConfig as E09SortConfig, FilterConfig as E09FilterConfig } from '../e09/e09-message/hooks';
import { useUser } from '../../hooks/useUser';
import Table from '../../common/components/Table/Table';
import { ContextMenuAction, RowEventHandlers, SortDirection } from '../../common/components/Table/types';
import { NotificationSnackbar } from '../../common/components/Snackbar';
import { useE55Download } from '../e55/e55-message/hooks/useE55Download';
import { useE55AcceptFile } from '../e55/e55-message/hooks/useE55AcceptFile';
import { useE53Download } from '../e53/e53-message/hooks/useE53Download';
import { useENO1Download } from '../eNO1/eNO1-message/hooks/useENO1Download';
import { useENO1AcceptFile } from '../eNO1/eNO1-message/hooks/useENO1AcceptFile';
import { useE09Download } from '../e09/e09-message/hooks/useE09Download';
import { useE09AcceptFile } from '../e09/e09-message/hooks/useE09AcceptFile';
import { useENO1Reject } from '../eNO1/eNO1-message/hooks/useENO1Reject';
import { useE55Reject } from '../e55/e55-message/hooks';
import { useE53Reject } from '../e53/e53-message/hooks/useE53Reject';
import { useE09Reject } from '../e09/e09-message/hooks/useE09Reject';
import CommentModal from '../../common/components/CommentModal';
import CommentWithTable, { DatasetInfo } from '../../common/components/CommentWithTable';
import ContactModal from '../../common/components/ContactModal';
import { getENO1Columns } from '../eNO1/eNO1-message/columns/eNO1Columns';
import { getE55Columns } from '../e55/e55-message/columns/e55Columns';
import { getE53Columns } from '../e53/e53-message/columns/e53Columns';
import { getE09Columns } from '../e09/e09-message/columns/e09Columns';
// Import comment hooks
import { useENO1Comments, useENO1SubmitComment } from '../eNO1/eNO1-message/hooks';
import { useE55Comments, useE55SubmitComment } from '../e55/e55-message/hooks';
import { useE53Comments, useE53SubmitComment } from '../e53/e53-message/hooks';
import { useE09Comments, useE09SubmitComment } from '../e09/e09-message/hooks';

// Extend the PendingAction interface from the imported one
declare module '../../types/modules' {
  interface PendingAction {
    pendingOn?: string;
    operatorByInboundPpo?: string;
    operatorByOutboundPpo?: string;
  }
}

// Define FilterGroup interface locally since it's not exported from SectionBar
interface FilterOption {
  value: string;
  label: string;
}

interface FilterGroup {
  id: string;
  label: string;
  type: 'select' | 'buttonGroup';
  multiple?: boolean;
  options: FilterOption[];
}

interface UserInfo {
  name: string;
  email: string;
  username: string;
}

interface Module {
  id: string | number;
  name: string;
  path?: string;
  url?: string;
  children?: Module[];
  permissions?: Permission[];
}

interface Permission {
  id: number;
  name: string;
  url: string;
}

interface Report {
  id: number;
  status: 'pending' | 'failed' | 'completed';
  ppo: string;
  year: number;
  quarter: string;
  version: number;
  uploadedBy: string | null;
  downloaded: string;
  outboundAcknowledged: string;
  inboundAcknowledged: string;
  samples: number;
  fileName: string;
  comment: boolean;
  removalPpo: string | null;
  removalDate: string | null;
}



const fetchReports = async (): Promise<Report[]> => {
  try {
    const response = await axios.get('/reports');
    return response.data;
  } catch (error) {
    console.error('Error fetching reports:', error);
    return [];
  }
};

const fetchOutboundReports = async (): Promise<Report[]> => {
  try {
    const response = await axios.get('/reports/outbound');
    return response.data;
  } catch (error) {
    console.error('Error fetching outbound reports:', error);
    return [];
  }
};

const fetchInboundReports = async (): Promise<Report[]> => {
  try {
    const response = await axios.get('/reports/inbound');
    return response.data;
  } catch (error) {
    console.error('Error fetching inbound reports:', error);
    return [];
  }
};


// Transform reports to PendingActions format
const transformReportsToPendingActions = (reports: Report[]): PendingAction[] => {
  const mapStatus = (status: Report['status']): 'F' | 'D' | 'V' => {
    switch (status) {
      case 'completed':
        return 'V';
      case 'failed':
        return 'F';
      case 'pending':
      default:
        return 'D';
    }
  };

  return reports?.map((report, index) => ({
    // Create a truly unique ID by adding index and random component
    id: `${report.id}-${report.ppo}-${report.year}-${report.quarter}-${report.version}-${index}-${Math.random().toString(36).substring(2, 9)}`,
    moduleId: 'reports',
    status: mapStatus(report.status),
    ppo: report.ppo,
    year: report.year,
    quarter: Number(report.quarter),
    version: report.version,
    uploadedBy: report.uploadedBy || 'Unknown',
    downloads: Number(report.downloaded),
    inboundAcceptance: new Date(report.inboundAcknowledged).toLocaleDateString('en-GB'),
    outboundAcceptance: new Date(report.outboundAcknowledged).toLocaleDateString('en-GB'),
    samples: report.samples,
    filename: report.fileName,
    comment: report.comment,
    removalDo: report.removalPpo || '',
    todo: 'Review',
    priority: 'medium',
    createdAt: new Date().toISOString()
  }));
};

export const Home: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // Read user data from cache only - UserInformationProvider handles fetching
  // This prevents duplicate requests - we only read from React Query cache
  const { data: userData, isLoading: isUserLoading, error: userError, refetch: refetchUser } = useUser(false);

  // Add snackbar state for notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // State for tracking deletion operation type
  const [deletionOperationType, setDeletionOperationType] = useState<string | null>(null);

  // Add state for comment modal
  const [commentModal, setCommentModal] = useState<{
    isOpen: boolean;
    fileId: number | null;
    moduleId: string | null;
  }>({
    isOpen: false,
    fileId: null,
    moduleId: null,
  });

  // Add state for CommentWithTable modal
  const [commentWithTableModal, setCommentWithTableModal] = useState<{
    isOpen: boolean;
    fileId: number | null;
    moduleId: string | null;
  }>({
    isOpen: false,
    fileId: null,
    moduleId: null,
  });

  // State for Contact modal
  const [contactModal, setContactModal] = useState<{
    isOpen: boolean;
    selectedRow: PendingAction | null;
    moduleId: string | null;
  }>({
    isOpen: false,
    selectedRow: null,
    moduleId: null,
  });

  // Add state for confirmation dialogs
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    confirmAction: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    confirmAction: () => {},
  });

  // Add state for comment pagination
  const [commentPagination, setCommentPagination] = useState({
    page: 0,
    size: 5
  });

  // Modified mutation hooks with appropriate error handlers
  const e55Download = useE55Download({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'E55 file downloaded successfully',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to download E55 file',
        severity: 'error',
      });
    },
  });

  const e55Finalize = useE55AcceptFile();

  const e53Download = useE53Download({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'E53 file downloaded successfully',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to download E53 file',
        severity: 'error',
      });
    },
  });

  const e53Finalize = useE53AcceptFile();
  
  const eNO1Download = useENO1Download({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'eNO1 file downloaded successfully',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to download eNO1 file',
        severity: 'error',
      });
    },
  });

  const eNO1Finalize = useENO1AcceptFile();

  const e09Download = useE09Download({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'E09 file downloaded successfully',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to download E09 file',
        severity: 'error',
      });
    },
  });

  const e09Finalize = useE09AcceptFile();

  // ENO1 Reject file hook
  const eno1Reject = useENO1Reject({
    onSuccess: () => {
      let successMessage = 'File deleted successfully';
      
      if (deletionOperationType === 'Mark File for Removal') {
        successMessage = 'File marked for removal successfully';
      } else if (deletionOperationType === 'Confirm Removal') {
        successMessage = 'File removed successfully';
      } else if (deletionOperationType === 'Undo Removal') {
        successMessage = 'Undid removal successfully';
      }
      
      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success',
      });
      
      // Reset operation type
      setDeletionOperationType(null);
      
      // Reload the page after a short delay to show the updated status
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    onError: (error) => {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : error.message || 'Unknown error';
      setSnackbar({
        open: true,
        message: `${errorMessage}`,
        severity: 'error',
      });
    },
  });

  // E55 Reject file hook
  const e55Reject = useE55Reject({
    onSuccess: () => {
      let successMessage = 'File deleted successfully';
      
      if (deletionOperationType === 'Mark File for Removal') {
        successMessage = 'File marked for removal successfully';
      } else if (deletionOperationType === 'Confirm Removal') {
        successMessage = 'File removed successfully';
      } else if (deletionOperationType === 'Undo Removal') {
        successMessage = 'Undid removal successfully';
      }
      
      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success',
      });
      
      // Reset operation type
      setDeletionOperationType(null);
      
      // Reload the page after a short delay to show the updated status
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    onError: (error) => {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : error.message || 'Unknown error';
      setSnackbar({
        open: true,
        message: `${errorMessage}`,
        severity: 'error',
      });
    },
  });

  // E53 Reject file hook
  const e53Reject = useE53Reject({
    onSuccess: () => {
      let successMessage = 'File deleted successfully';
      
      if (deletionOperationType === 'Mark File for Removal') {
        successMessage = 'File marked for removal successfully';
      } else if (deletionOperationType === 'Confirm Removal') {
        successMessage = 'File removed successfully';
      } else if (deletionOperationType === 'Undo Removal') {
        successMessage = 'Undid removal successfully';
      }
      
      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success',
      });
      
      // Reset operation type
      setDeletionOperationType(null);
      
      // Reload the page after a short delay to show the updated status
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    onError: (error: Error) => {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : error.message || 'Unknown error';
      setSnackbar({
        open: true,
        message: `${errorMessage}`,
        severity: 'error',
      });
    },
  });

  // E09 Reject file hook
  const e09Reject = useE09Reject({
    onSuccess: () => {
      let successMessage = 'File deleted successfully';
      
      if (deletionOperationType === 'Mark File for Removal') {
        successMessage = 'File marked for removal successfully';
      } else if (deletionOperationType === 'Confirm Removal') {
        successMessage = 'File removed successfully';
      } else if (deletionOperationType === 'Undo Removal') {
        successMessage = 'Undid removal successfully';
      }
      
      setSnackbar({
        open: true,
        message: successMessage,
        severity: 'success',
      });
      
      // Reset operation type
      setDeletionOperationType(null);
      
      // Reload the page after a short delay to show the updated status
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    },
    onError: (error: Error) => {
      const errorMessage = axios.isAxiosError(error) && error.response?.data?.message
        ? error.response.data.message
        : error.message || 'Unknown error';
      setSnackbar({
        open: true,
        message: `${errorMessage}`,
        severity: 'error',
      });
    },
  });

  // Helper function to check module permissions
  const hasModulePermission = useCallback(
    (moduleId: string, permissionName: string) => {
      if (!userData || !userData.modules) return false;

      // Find the specific module
      const module = userData.modules.find((m) => {
        // Match module name based on moduleId (case insensitive)
        const moduleName = m.name.toLowerCase();
        if (moduleId === 'eNO1') {
          return moduleName === 'eno1 sampling' && m.enabled === true;
        } else if (moduleId === 'e55') {
          return (moduleName === 'e55 weights' || moduleName === 'e55 messages') && m.enabled === true;
        } else if (moduleId === 'e53') {
          return (moduleName === 'e53 sampling' || moduleName === 'e53 messages') && m.enabled === true;
        } else if (moduleId === 'e09') {
          return (moduleName === 'e09 ibrs' || moduleName === 'e09 messages') && m.enabled === true;
        }
        return false;
      });

      // Check if module exists, is enabled, and has permissions
      if (!module || !module.permissions || module.enabled !== true) return false;

      // Check if the user has the specific permission and it's enabled
      return module.permissions.some(
        (permission) => permission.name === permissionName && permission.enabled
      );
    },
    [userData]
  );

  // Helper function to check if user has access to the module
  const hasModuleAccess = useCallback(
    (moduleId: string) => {
      if (!userData || !userData.modules) return false;

      // Find the specific module and check if it's enabled
      return userData.modules.some((m) => {
        const moduleName = m.name.toLowerCase();
        if (moduleId === 'eNO1') {
          return moduleName === 'eno1 sampling' && m.enabled === true;
        } else if (moduleId === 'e55') {
          return (moduleName === 'e55 weights' || moduleName === 'e55 messages') && m.enabled === true;
        } else if (moduleId === 'e53') {
          return (moduleName === 'e53 sampling' || moduleName === 'e53 messages') && m.enabled === true;
        } else if (moduleId === 'e09') {
          return (moduleName === 'e09 ibrs' || moduleName === 'e09 messages') && m.enabled === true;
        }
        return false;
      });
    },
    [userData]
  );

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Function to determine row background color based on inbound/outbound
  const getRowBackgroundColor = useCallback((row: PendingAction): string => {
    // Check if operatorByInboundPpo equals current user's designated operator
    const isInbound = row.operatorByInboundPpo === userData?.designatedOperator;
    
    // Return green for inbound, red for outbound
    return isInbound ? '#e8f5e9' : '#ffebee';
  }, [userData?.designatedOperator]);

  const handleContextMenuAction = useCallback(
    async (action: ContextMenuAction, row: PendingAction) => {
      const fileId = Number(row.id.split('-')[0]);
      const moduleId = row.moduleId;
      
      // Helper function to determine if the file is pending on the user
      const isFilePendingOnUser = () => {

        // First check if pendingOn property exists and matches user's PPO
        if (row.pendingOn && row.pendingOn === userData?.designatedOperator) {
          return true;
        }
        
        // For eNO1 module
        if (moduleId === 'eNO1') {
          // Check if status contains DRAFT and outbound acceptance is not set
          const status = String(row.status || '').toUpperCase();
          const isEditable = (status.includes('DRAFT') || status === 'D') && row.outboundAcceptance === '-';
          
          if (isEditable) {
            return true;
          }
        }
        
        // Additional fallback for other modules
        // If the file is in draft state and no acceptance dates, it's likely editable
        const status = String(row.status || '').toUpperCase();
        if ((status.includes('DRAFT') || status === 'D') && 
            row.outboundAcceptance === '-' && 
            row.inboundAcceptance === '-') {
          return true;
        }
        
        return false;
      };
      
      // Helper function to check if file is FINAL and marked for removal
      const isFileMarkedForRemoval = () => {
        const status = String(row.status || '').toUpperCase();
        const isFinal = status === 'F' || status === 'FINAL';
        const hasRemovalDo = row.removalDo && row.removalDo.trim() !== '';
        return isFinal && hasRemovalDo;
      };
      
      // Helper function to get the appropriate delete confirmation message
      const getDeleteConfirmation = () => {
        if (isFileMarkedForRemoval()) {
          // File is FINAL and already marked for removal
          if (row.removalDo === userData?.designatedOperator) {
            // Same DO - ask if they want to undo removal
            return {
              title: 'Undo Removal',
              message: 'Do you want to undo the removal of this file?',
              requiresComment: false
            };
          } else {
            // Different DO - ask to confirm removal
            return {
              title: 'Confirm Removal',
              message: 'Do you confirm removal of this file?',
              requiresComment: false
            };
          }
        }
        
        // Check if file is FINAL but not marked for removal
        const status = String(row.status || '').toUpperCase();
        const isFinal = status === 'F' || status === 'FINAL';
        
        if (isFinal) {
          // FINAL file, not marked for removal - require comment
          return {
            title: 'Mark File for Removal',
            message: 'Are you sure you want to mark this file for removal?',
            requiresComment: true
          };
        }
        
        // Non-FINAL file - standard delete
        return {
          title: 'Draft File Removal',
          message: 'Are you sure you want to delete this file?',
          requiresComment: false
        };
      };
      
      try {
        switch (action) {
          case 'ViewDespatches':
          case 'ViewSamples':
            if (moduleId === 'eNO1') {
              // Special case for eNO1 - check permission directly like in eNO1Message
              const eno1Module = userData?.modules?.find(m => m.name === 'eNO1 Sampling');
              const hasViewPermission = eno1Module?.permissions?.some(
                p => p.name === 'eNO1 View Samples' && p.enabled
              );
              
              if (hasViewPermission) {
                navigate(`/eno1/messages/samples/${fileId}`);
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to view samples',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e55') {
              // Special case for e55 - check permission directly like in e55Message
              const e55Module = userData?.modules?.find(m => m.name === 'e55 Weights');
              const hasViewPermission = e55Module?.permissions?.some(
                p => p.name === 'View Despatches' && p.enabled
              );
              
              if (hasViewPermission) {
                navigate(`/e55/despatches/${fileId}`);
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to view samples',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e53') {
              // Special case for e53 - check permission directly like in e53Message
              const e53Module = userData?.modules?.find(m => m.name === 'e53 Sampling');
              const hasViewPermission = e53Module?.permissions?.some(
                p => p.name === 'e53 View Samples' && p.enabled
              );
              
              if (hasViewPermission) {
                navigate(`/e53/samples/${fileId}`);
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to view e53 samples',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e09') {
              // Special case for e09 - check permission directly like in e09Message
              const e09Module = userData?.modules?.find(m => m.name === 'e09 IBRS');
              const hasViewPermission = e09Module?.permissions?.some(
                p => p.name === 'View Despatches' && p.enabled
              );
              
              if (hasViewPermission) {
                navigate(`/e09/despatches/${fileId}`);
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to view e09 samples',
                  severity: 'error',
                });
              }
            } else {
              // For other modules, use the existing permission check
              const viewPermName = `${moduleId} View Samples`;
              if (hasModulePermission(moduleId, viewPermName)) {
                navigate(`/${moduleId.toLowerCase()}/view/${row.id}`);
              } else {
                setSnackbar({
                  open: true,
                  message: `You do not have permission to view ${moduleId} samples`,
                  severity: 'error',
                });
              }
            }
            break;

          case 'editSamples':
            if (moduleId === 'eNO1') {
              // Special case for eNO1 - check permission directly like in eNO1Message
              const eno1Module = userData?.modules?.find(m => m.name === 'eNO1 Sampling');
              
              const hasEditPermission = eno1Module?.permissions?.some(
                p => p.name === 'eNO1 Edit Samples' && p.enabled
              );
              
              
              if (hasEditPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  navigate(`/eno1/messages/samples/${fileId}?editMode=true`);
                } else {
                  setSnackbar({
                    open: true,
                    message: "This file has already been agreed on by both PPO's. No further editing is allowed.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to edit samples',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e55') {
              // Special case for e55 - check permission directly like in e55Message
              const e55Module = userData?.modules?.find(m => m.name === 'e55 Weights');
              
              const hasEditPermission = e55Module?.permissions?.some(
                p => p.name === 'Edit Despatches' && p.enabled
              );
              

              
              if (hasEditPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  navigate(`/e55/despatches/${fileId}?editMode=true`);
                } else {
                  setSnackbar({
                    open: true,
                    message: "Sorry you can't edit this file, only the other PPO can make amendments.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to edit samples',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e53') {
              // Special case for e53 - check permission directly like in e53Message
              const e53Module = userData?.modules?.find(m => m.name === 'e53 Sampling');
              
              const hasEditPermission = e53Module?.permissions?.some(
                p => p.name === 'e53 Edit Samples' && p.enabled
              );
              
              if (hasEditPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  navigate(`/e53/samples/${fileId}?editMode=true`);
                } else {
                  setSnackbar({
                    open: true,
                    message: "This file has already been agreed on by both PPO's. No further editing is allowed.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to edit e53 samples',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e09') {
              // Special case for e09 - check permission directly like in e09Message
              const e09Module = userData?.modules?.find(m => m.name === 'e09 IBRS');
              
              const hasEditPermission = e09Module?.permissions?.some(
                p => p.name === 'Edit Despatches' && p.enabled
              );
              
              if (hasEditPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  navigate(`/e09/despatches/${fileId}?editMode=true`);
                } else {
                  setSnackbar({
                    open: true,
                    message: "This file has already been agreed on by both PPO's. No further editing is allowed.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to edit e09 samples',
                  severity: 'error',
                });
              }
            } else {
              // For other modules, use the existing permission check
              const editPermName = `${moduleId} Edit Samples`;
              
              if (hasModulePermission(moduleId, editPermName)) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  navigate(`/${moduleId.toLowerCase()}/edit/${row.id}`);
                } else {
                  setSnackbar({
                    open: true,
                    message: "This file has already been agreed on by both PPO's. No further editing is allowed.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: `You do not have permission to edit ${moduleId} samples`,
                  severity: 'error',
                });
              }
            }
            break;

          case 'download':
            switch (moduleId) {
              case 'eNO1':
                await eNO1Download.mutateAsync({fileId});
                break;
              case 'e55':
                await e55Download.mutateAsync(fileId);
                break;
              case 'e53':
                await e53Download.mutateAsync(fileId);
                break;
              case 'e09':
                await e09Download.mutateAsync(fileId);
                break;
            }
            break;

          case 'accept':
            if (moduleId === 'eNO1') {
              // Special case for eNO1 - check permission directly like in eNO1Message
              const eno1Module = userData?.modules?.find(m => m.name === 'eNO1 Sampling');
              
              const hasAcceptPermission = eno1Module?.permissions?.some(
                p => p.name === 'eNO1 Edit Samples' && p.enabled
              );

              
              if (hasAcceptPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  try {
                    await eNO1Finalize.mutateAsync(fileId);
                    // Handle success message manually
                    setSnackbar({
                      open: true,
                      message: 'eNO1 file accepted successfully',
                      severity: 'success',
                    });
                    setTimeout(() => {
                      window.location.reload();
                    }, 1500);
                  } catch (error) {
                    setSnackbar({
                      open: true,
                      message: `Failed to accept file: ${(error as Error).message || 'Unknown error'}`,
                      severity: 'error',
                    });
                  }
                } else {
                  setSnackbar({
                    open: true,
                    message: "This file has already been agreed on by both PPO's. No further editing is allowed.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to accept files',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e55') {
              // Special case for e55 - check permission directly like in e55Message
              const e55Module = userData?.modules?.find(m => m.name === 'e55 Weights');
              
              const hasAcceptPermission = e55Module?.permissions?.some(
                p => p.name === 'Edit Despatches' && p.enabled
              );
              
              
              if (hasAcceptPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  try {
                    await e55Finalize.mutateAsync(fileId);
                    // Handle success message manually
                    setSnackbar({
                      open: true,
                      message: 'E55 file accepted successfully',
                      severity: 'success',
                    });
                    setTimeout(() => {
                      window.location.reload();
                    }, 1500);
                  } catch (error) {
                    setSnackbar({
                      open: true,
                      message: `Failed to accept file: ${(error as Error).message || 'Unknown error'}`,
                      severity: 'error',
                    });
                  }
                } else {
                  setSnackbar({
                    open: true,
                    message: "Sorry you can't accept this file, only the other PPO can accept it.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to accept files',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e53') {
              // Special case for e53 - check permission directly like in e53Message
              const e53Module = userData?.modules?.find(m => m.name === 'e53 Sampling');
              
              const hasAcceptPermission = e53Module?.permissions?.some(
                p => p.name === 'e53 Edit Samples' && p.enabled
              );
              
              if (hasAcceptPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  try {
                    await e53Finalize.mutateAsync(fileId);
                    // Handle success message manually
                    setSnackbar({
                      open: true,
                      message: 'e53 file accepted successfully',
                      severity: 'success',
                    });
                    setTimeout(() => {
                      window.location.reload();
                    }, 1500);
                  } catch (error) {
                    setSnackbar({
                      open: true,
                      message: `Failed to accept file: ${(error as Error).message || 'Unknown error'}`,
                      severity: 'error',
                    });
                  }
                } else {
                  setSnackbar({
                    open: true,
                    message: "This file has already been agreed on by both PPO's. No further editing is allowed.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to accept e53 files',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e09') {
              // Special case for e09 - check permission directly like in e09Message
              const e09Module = userData?.modules?.find(m => m.name === 'e09 IBRS');
              
              const hasAcceptPermission = e09Module?.permissions?.some(
                p => p.name === 'Edit Despatches' && p.enabled
              );
              
              if (hasAcceptPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  try {
                    await e09Finalize.mutateAsync(fileId);
                    // Handle success message manually
                    setSnackbar({
                      open: true,
                      message: 'E09 file accepted successfully',
                      severity: 'success',
                    });
                    setTimeout(() => {
                      window.location.reload();
                    }, 1500);
                  } catch (error) {
                    setSnackbar({
                      open: true,
                      message: `Failed to accept file: ${(error as Error).message || 'Unknown error'}`,
                      severity: 'error',
                    });
                  }
                } else {
                  setSnackbar({
                    open: true,
                    message: "This file has already been agreed on by both PPO's. No further editing is allowed.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to accept e09 files',
                  severity: 'error',
                });
              }
            } else {
              // For other modules, use the existing permission check
              const acceptPermName = `${moduleId} Edit Samples`;
              
              if (hasModulePermission(moduleId, acceptPermName)) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  setSnackbar({
                    open: true,
                    message: `Accept action not implemented for ${moduleId}`,
                    severity: 'info',
                  });
                } else {
                  setSnackbar({
                    open: true,
                    message: "This file has already been agreed on by both PPO's. No further editing is allowed.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: `You do not have permission to accept ${moduleId} files`,
                  severity: 'error',
                });
              }
            }
            break;
            
          case 'delete':
            if (moduleId === 'eNO1') {
              // Special case for eNO1 - check permission directly like in eNO1Message
              const eno1Module = userData?.modules?.find(m => m.name === 'eNO1 Sampling');
              
              const hasRejectPermission = eno1Module?.permissions?.some(
                p => p.name === 'Delete eNO1 Files' && p.enabled
              );
              
              
              if (hasRejectPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  // Get the appropriate confirmation message
                  const confirmation = getDeleteConfirmation();
                  
                  // Show confirmation dialog
                  setConfirmDialog({
                    isOpen: true,
                    title: confirmation.title,
                    message: confirmation.message,
                    confirmAction: () => {
                      // Set the operation type for success message
                      setDeletionOperationType(confirmation.title);
                      
                      if (confirmation.requiresComment) {
                        // Open comment modal for FINAL files not yet marked for removal
                        setCommentModal({
                          isOpen: true,
                          fileId,
                          moduleId
                        });
                      } else {
                        // Delete directly without comment
                        eno1Reject.mutate({
                          fileId,
                          comment: undefined
                        });
                      }
                    }
                  });
                } else {
                  setSnackbar({
                    open: true,
                    message: "This file has already been agreed on by both PPO's. No further editing is allowed.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to delete files',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e55') {
              // Special case for e55 - check permission directly like in e55Message
              const e55Module = userData?.modules?.find(m => m.name === 'e55 Weights');
              
              const hasRejectPermission = e55Module?.permissions?.some(
                p => p.name === 'Delete e55 Files' && p.enabled
              );
              
              if (hasRejectPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  // Get the appropriate confirmation message
                  const confirmation = getDeleteConfirmation();
                  
                  // Show confirmation dialog
                  setConfirmDialog({
                    isOpen: true,
                    title: confirmation.title,
                    message: confirmation.message,
                    confirmAction: () => {
                      // Set the operation type for success message
                      setDeletionOperationType(confirmation.title);
                      
                      if (confirmation.requiresComment) {
                        // Open comment modal for FINAL files not yet marked for removal
                        setCommentModal({
                          isOpen: true,
                          fileId,
                          moduleId
                        });
                      } else {
                        // Delete directly without comment
                        e55Reject.mutate({
                          fileId,
                          comment: undefined
                        });
                      }
                    }
                  });
                } else {
                  setSnackbar({
                    open: true,
                    message: "Sorry you can't delete this file, only the other PPO can delete it.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to delete files',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e53') {
              // For E53 module
              const e53Module = userData?.modules?.find(m => m.name === 'e53 Sampling');
              
              const hasRejectPermission = e53Module?.permissions?.some(
                p => p.name === 'Delete e53 files' && p.enabled
              );
              
              if (hasRejectPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  // Get the appropriate confirmation message
                  const confirmation = getDeleteConfirmation();
                  
                  // Show confirmation dialog
                  setConfirmDialog({
                    isOpen: true,
                    title: confirmation.title,
                    message: confirmation.message,
                    confirmAction: () => {
                      // Set the operation type for success message
                      setDeletionOperationType(confirmation.title);
                      
                      if (confirmation.requiresComment) {
                        // Open comment modal for FINAL files not yet marked for removal
                        setCommentModal({
                          isOpen: true,
                          fileId,
                          moduleId
                        });
                      } else {
                        // Delete directly without comment
                        e53Reject.mutate({
                          fileId,
                          comment: undefined
                        });
                      }
                    }
                  });
                } else {
                  setSnackbar({
                    open: true,
                    message: "This file has already been agreed on by both PPO's. No further editing is allowed.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to delete e53 files',
                  severity: 'error',
                });
              }
            } else if (moduleId === 'e09') {
              // For E09 module
              const e09Module = userData?.modules?.find(m => m.name === 'e09 IBRS');
              
              const hasRejectPermission = e09Module?.permissions?.some(
                p => p.name === 'Delete e09 Files' && p.enabled
              );
              
              if (hasRejectPermission) {
                // Check if the file is pending on the user's PPO
                if (isFilePendingOnUser()) {
                  // Get the appropriate confirmation message
                  const confirmation = getDeleteConfirmation();
                  
                  // Show confirmation dialog
                  setConfirmDialog({
                    isOpen: true,
                    title: confirmation.title,
                    message: confirmation.message,
                    confirmAction: () => {
                      // Set the operation type for success message
                      setDeletionOperationType(confirmation.title);
                      
                      if (confirmation.requiresComment) {
                        // Open comment modal for FINAL files not yet marked for removal
                        setCommentModal({
                          isOpen: true,
                          fileId,
                          moduleId
                        });
                      } else {
                        // Delete directly without comment
                        e09Reject.mutate({
                          fileId,
                          comment: undefined
                        });
                      }
                    }
                  });
                } else {
                  setSnackbar({
                    open: true,
                    message: "This file has already been agreed on by both PPO's. No further editing is allowed.",
                    severity: 'error',
                  });
                }
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to delete e09 files',
                  severity: 'error',
                });
              }
            } else {
              // For other modules, show 'not implemented' message
              setSnackbar({
                open: true,
                message: `Delete action not implemented for ${moduleId}`,
                severity: 'info',
              });
            }
            break;

          case 'no2Report':
            if (moduleId === 'eNO1') {
              // Check if user has permission to view NO2 reports
              const eno1Module = userData?.modules?.find(m => m.name === 'eNO1 Sampling');
              const hasReportPermission = eno1Module?.permissions?.some(
                p => p.name === 'NO2 Report' && p.enabled
              );
              
              if (hasReportPermission) {
                // Build query parameters from row data
                const queryParams = new URLSearchParams();
                
                // Set PPO filter based on the row's PPO
                queryParams.set('ppo', row.ppo || 'All');
                
                // Set period range based on the row's year and quarter
                if (row.year && row.quarter) {
                  const fromYear = row.year;
                  const toYear = row.year;
                  const fromQuarter = row.quarter;
                  const toQuarter = row.quarter;
                  queryParams.set('fromYear', fromYear.toString());
                  queryParams.set('toYear', toYear.toString());
                  queryParams.set('fromQuarter', fromQuarter.toString());
                  queryParams.set('toQuarter', toQuarter.toString());
                }
                
                // Navigate to NO2 report with the constructed query parameters
                navigate(`/eno1/reports/no2?${queryParams.toString()}`);
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to view NO2 reports',
                  severity: 'error',
                });
              }
            } else {
              setSnackbar({
                open: true,
                message: 'NO2 Report is only available for eNO1 module',
                severity: 'info',
              });
            }
            break;

          case 'no3Report':
            if (moduleId === 'eNO1') {
              // Check if user has permission to view NO3 reports
              const eno1Module = userData?.modules?.find(m => m.name === 'eNO1 Sampling');
              const hasReportPermission = eno1Module?.permissions?.some(
                p => p.name === 'NO3 Report' && p.enabled
              );
              
              if (hasReportPermission) {
                // Build query parameters from row data
                const queryParams = new URLSearchParams();
                // Set PPO filter based on the conditional logic
                const ppoValue = row.operatorByInboundPpo === userData?.designatedOperator 
                  ? row.operatorByOutboundPpo 
                  : row.operatorByInboundPpo;
                queryParams.set('ppo', ppoValue || 'All');
                
                // Set type to inbound for NO3 report
                queryParams.set('type', `${row.pendingOn === userData?.designatedOperator ? 'inbound' : 'outbound'}`);
                
                // Set year based on the row's year
                if (row.year) {
                  queryParams.set('year', row.year.toString());
                }
                
                // Navigate to NO3 report with the constructed query parameters
                navigate(`/eno1/reports/no3?${queryParams.toString()}`);
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to view NO3 reports',
                  severity: 'error',
                });
              }
            } else {
              setSnackbar({
                open: true,
                message: 'NO3 Report is only available for eNO1 module',
                severity: 'info',
              });
            }
            break;

          case 'cn54Report':
            if (moduleId === 'e53') {
              // Check if user has permission to view CN54 reports
              const e53Module = userData?.modules?.find(m => m.name === 'e53 Sampling');
              const hasReportPermission = e53Module?.permissions?.some(
                p => p.name === 'CN54 Report' && p.enabled
              );
              
              if (hasReportPermission) {
                // Build query parameters from row data
                const queryParams = new URLSearchParams();
                
                // Set PPO filter based on the row's PPO
                queryParams.set('ppo', row.ppo || 'All');
                
                // Set period range based on the row's year and quarter
                if (row.year && row.quarter) {
                  const fromYear = row.year;
                  const toYear = row.year;
                  const fromQuarter = row.quarter;
                  const toQuarter = row.quarter;
                  queryParams.set('fromYear', fromYear.toString());
                  queryParams.set('toYear', toYear.toString());
                  queryParams.set('fromQuarter', fromQuarter.toString());
                  queryParams.set('toQuarter', toQuarter.toString());
                }
                
                // Set table type to 'inbound' for CN54 report
                queryParams.set('table', 'inbound');
                
                // Navigate to CN54 report with the constructed query parameters
                navigate(`/e53/reports/cn54?${queryParams.toString()}`);
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to view CN54 reports',
                  severity: 'error',
                });
              }
            } else {
              setSnackbar({
                open: true,
                message: 'CN54 Report is only available for e53 module',
                severity: 'info',
              });
            }
            break;

          case 'cn56Report':
            if (moduleId === 'e55') {
              // Check if user has permission to view CN56 reports
              const e55Module = userData?.modules?.find(m => m.name === 'e55 Weights');
              const hasReportPermission = e55Module?.permissions?.some(
                p => p.name === 'CN56 Report' && p.enabled
              );
              
              if (hasReportPermission) {
                // Build query parameters from row data
                const queryParams = new URLSearchParams();
                
                // Set PPO filter based on the row's PPO
                queryParams.set('ppo', row.ppo || 'All');
                
                // Set period range based on the row's year and quarter
                if (row.year && row.quarter) {
                  const fromYear = row.year;
                  const toYear = row.year;
                  const fromQuarter = row.quarter;
                  const toQuarter = row.quarter;
                  queryParams.set('fromYear', fromYear.toString());
                  queryParams.set('toYear', toYear.toString());
                  queryParams.set('fromQuarter', fromQuarter.toString());
                  queryParams.set('toQuarter', toQuarter.toString());
                }
                
                // Set table type to 'inbound' for CN56 report
                queryParams.set('table', 'inbound');
                
                // Navigate to CN56 report with the constructed query parameters
                navigate(`/e55/reports/cn56?${queryParams.toString()}`);
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to view CN56 reports',
                  severity: 'error',
                });
              }
            } else {
              setSnackbar({
                open: true,
                message: 'CN56 Report is only available for e55 module',
                severity: 'info',
              });
            }
            break;

          case 'cn10Report':
            if (moduleId === 'e09') {
              // Check if user has permission to view CN10 reports
              const e09Module = userData?.modules?.find(m => m.name === 'e09 IBRS');
              const hasReportPermission = e09Module?.permissions?.some(
                p => p.name === 'CN10 Report' && p.enabled
              );
              
              if (hasReportPermission) {
                // Build query parameters from row data
                const queryParams = new URLSearchParams();
                
                // Set PPO filter based on the row's PPO
                queryParams.set('ppo', row.ppo || 'All');
                
                // Set period range based on the row's year and quarter
                if (row.year && row.quarter) {
                  // Format quarter to match CN10Report expectations (Q1, Q2, Q3, Q4)
                  const formattedQuarter = `Q${row.quarter}`;
                  const fromPeriod = `${row.year}-${formattedQuarter}`;
                  const toPeriod = `${row.year}-${formattedQuarter}`;
                  queryParams.set('fromPeriod', fromPeriod);
                  queryParams.set('toPeriod', toPeriod);
                }
                
                // Set type parameter to 'inbound'
                queryParams.set('type', 'inbound');
                
                // Set mail categories (default to both A and D)
                queryParams.set('mailCategories', 'A,D');
                
                // Navigate to CN10 report with the constructed query parameters
                navigate(`/e09/reports/cn10?${queryParams.toString()}`);
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to view CN10 reports',
                  severity: 'error',
                });
              }
            } else {
              setSnackbar({
                open: true,
                message: 'CN10 Report is only available for e09 module',
                severity: 'info',
              });
            }
            break;

          case 'cn19Report':
            if (moduleId === 'e09') {
              // Check if user has permission to view CN19 reports
              const e09Module = userData?.modules?.find(m => m.name === 'e09 IBRS');
              const hasReportPermission = e09Module?.permissions?.some(
                p => p.name === 'CN19 Report' && p.enabled
              );
              
              if (hasReportPermission) {
                // Build query parameters from row data
                const queryParams = new URLSearchParams();
                
                // Set PPO filter based on the row's PPO
                queryParams.set('ppo', row.ppo || 'All');
                
                // Set year parameter from the row's accounting year
                if (row.year) {
                  queryParams.set('year', row.year.toString());
                }
                
                // Navigate to CN19 report with the constructed query parameters
                navigate(`/e09/reports/cn19?${queryParams.toString()}`);
              } else {
                setSnackbar({
                  open: true,
                  message: 'You do not have permission to view CN19 reports',
                  severity: 'error',
                });
              }
            } else {
              setSnackbar({
                open: true,
                message: 'CN19 Report is only available for e09 module',
                severity: 'info',
              });
            }
            break;

          case 'comment':
            // Reset pagination and open CommentWithTable modal to display file information and collect comment
            setCommentPagination({
              page: 0,
              size: 5
            });
            setCommentWithTableModal({
              isOpen: true,
              fileId,
              moduleId
            });
            break;

          case 'contact':
            // Open Contact modal to display counterpart emails
            setContactModal({
              isOpen: true,
              selectedRow: row,
              moduleId,
            });
            break;
            
          default:
            break;
        }
      } catch (error) {
        console.error('Error handling action:', error);
        setSnackbar({
          open: true,
          message: `An error occurred: ${(error as Error).message || 'Unknown error'}`,
          severity: 'error',
        });
      }
    },
    [
      navigate, 
      eNO1Download, 
      eNO1Finalize, 
      e55Download, 
      e55Finalize, 
      e53Download, 
      e53Finalize, 
      e09Download, 
      e09Finalize, 
      hasModulePermission, 
      userData,
      eno1Reject,
      e55Reject,
      e53Reject,
      e09Reject,
      setCommentModal
    ]
  );

  // Add handlers for comment modal
  const handleCloseCommentModal = () => {
    setCommentModal({
      isOpen: false,
      fileId: null,
      moduleId: null
    });
  };

  const handleCommentSubmit = (comment: string) => {
    if (commentModal.fileId && comment.trim()) {
      if (commentModal.moduleId === 'eNO1') {
        // Call eNO1 reject API with file ID and comment
        eno1Reject.mutate({
          fileId: commentModal.fileId,
          comment: comment.trim(),
        });
      } else if (commentModal.moduleId === 'e55') {
        // Call e55 reject API with file ID and comment
        e55Reject.mutate({
          fileId: commentModal.fileId,
          comment: comment.trim(),
        });
      } else if (commentModal.moduleId === 'e53') {
        // Call e53 reject API with file ID and comment
        e53Reject.mutate({
          fileId: commentModal.fileId,
          comment: comment.trim(),
        });
      } else if (commentModal.moduleId === 'e09') {
        // Call e09 reject API with file ID and comment
        e09Reject.mutate({
          fileId: commentModal.fileId,
          comment: comment.trim(),
        });
      }
      // Close the modal
      handleCloseCommentModal();
    }
  };

  // Add handlers for CommentWithTable modal
  const handleCloseCommentWithTableModal = () => {
    setCommentWithTableModal({
      isOpen: false,
      fileId: null,
      moduleId: null
    });
    // Reset pagination when closing modal
    setCommentPagination({
      page: 0,
      size: 5
    });
  };

  // Add pagination handlers for comments
  const handleCommentPageChange = (page: number) => {
    setCommentPagination(prev => ({
      ...prev,
      page
    }));
  };

  const handleCommentPageSizeChange = (size: number) => {
    setCommentPagination({
      page: 0, // Reset to first page when changing page size
      size
    });
  };

  // Add handlers for confirmation dialog
  const handleCloseConfirmDialog = () => {
    setConfirmDialog({
      isOpen: false,
      title: '',
      message: '',
      confirmAction: () => {},
    });
  };

  const handleConfirmAction = () => {
    // Execute the stored action
    confirmDialog.confirmAction();
    // Close the dialog
    handleCloseConfirmDialog();
  };

  const handleCommentWithTableSubmit = (comment: string) => {
    if (!commentWithTableModal.fileId || !commentWithTableModal.moduleId || !comment.trim()) return;
    
    if (commentWithTableModal.moduleId === 'eNO1') {
      eno1SubmitComment.mutate({
        dataSetId: commentWithTableModal.fileId,
        comment: comment.trim()
      });
    } else if (commentWithTableModal.moduleId === 'e55') {
      e55SubmitComment.mutate({
        dataSetId: commentWithTableModal.fileId,
        comment: comment.trim()
      });
    } else if (commentWithTableModal.moduleId === 'e53') {
      e53SubmitComment.mutate({
        dataSetId: commentWithTableModal.fileId,
        comment: comment.trim()
      });
    } else if (commentWithTableModal.moduleId === 'e09') {
      e09SubmitComment.mutate({
        dataSetId: commentWithTableModal.fileId,
        comment: comment.trim()
      });
    }
  };

  // Add state for sorting
  const [eno1SortColumn, setEno1SortColumn] = useState<string | undefined>(undefined);
  const [eno1SortDirection, setEno1SortDirection] = useState<SortDirection | undefined>(undefined);
  const [eno1SearchColumn, setEno1SearchColumn] = useState<string | undefined>(undefined);
  const [eno1SearchTerm, setEno1SearchTerm] = useState<string | undefined>(undefined);
  const [e55SortColumn, setE55SortColumn] = useState<string | undefined>(undefined);
  const [e55SortDirection, setE55SortDirection] = useState<SortDirection | undefined>(undefined);
  const [e55SearchColumn, setE55SearchColumn] = useState<string | undefined>(undefined);
  const [e55SearchTerm, setE55SearchTerm] = useState<string | undefined>(undefined);
  const [e53SortColumn, setE53SortColumn] = useState<string | undefined>(undefined);
  const [e53SortDirection, setE53SortDirection] = useState<SortDirection | undefined>(undefined);
  const [e53SearchColumn, setE53SearchColumn] = useState<string | undefined>(undefined);
  const [e53SearchTerm, setE53SearchTerm] = useState<string | undefined>(undefined);
  const [e09SortColumn, setE09SortColumn] = useState<string | undefined>(undefined);
  const [e09SortDirection, setE09SortDirection] = useState<SortDirection | undefined>(undefined);
  const [e09SearchColumn, setE09SearchColumn] = useState<string | undefined>(undefined);
  const [e09SearchTerm, setE09SearchTerm] = useState<string | undefined>(undefined);

  // State for multiple sorts and filters for each module
  const [eno1Sorts, setEno1Sorts] = useState<ENO1SortConfig[]>([]);
  const [eno1Filters, setEno1Filters] = useState<ENO1FilterConfig[]>([]);
  const [e55Sorts, setE55Sorts] = useState<E55SortConfig[]>([]);
  const [e55Filters, setE55Filters] = useState<E55FilterConfig[]>([]);
  const [e53Sorts, setE53Sorts] = useState<E53SortConfig[]>([]);
  const [e53Filters, setE53Filters] = useState<E53FilterConfig[]>([]);
  const [e09Sorts, setE09Sorts] = useState<E09SortConfig[]>([]);
  const [e09Filters, setE09Filters] = useState<E09FilterConfig[]>([]);

  // Helper function to parse multiple sorts from URL
  const parseSortsFromURL = useCallback((params: URLSearchParams, prefix: string): ENO1SortConfig[] | E55SortConfig[] | E53SortConfig[] | E09SortConfig[] => {
    const parsedSorts: any[] = [];
    params.getAll(`${prefix}_sort`).forEach(sortParam => {
      const [column, direction] = sortParam.split(',');
      if (column && direction) {
        parsedSorts.push({ column, direction: direction as 'ASC' | 'DESC' });
      }
    });
    return parsedSorts;
  }, []);

  // Helper function to update URL with multiple sorts
  const updateSortsInURL = useCallback((newSorts: any[], params: URLSearchParams, prefix: string) => {
    params.delete(`${prefix}_sort`); // Remove all existing sort parameters
    newSorts.forEach((sort) => {
      params.append(`${prefix}_sort`, `${sort.column},${sort.direction}`);
    });
  }, []);

  // Helper function to parse multiple filters from URL
  const parseFiltersFromURL = useCallback((params: URLSearchParams, prefix: string): ENO1FilterConfig[] | E55FilterConfig[] | E53FilterConfig[] | E09FilterConfig[] => {
    const parsedFilters: any[] = [];
    // Check for filter parameters with the prefix pattern (e.g., eNO1_filter0Col, eNO1_filter0Term)
    const filterKeys = Array.from(params.keys()).filter(key =>
      key.startsWith(`${prefix}_filter`) && key.endsWith('Col')
    );

    filterKeys.forEach(key => {
      const colSuffix = key.replace(`${prefix}_filter`, '').replace('Col', '');
      const colValue = params.get(key);
      const termKey = `${prefix}_filter${colSuffix}Term`;
      const termValue = params.get(termKey);

      if (colValue && termValue) {
        parsedFilters.push({ column: colValue, term: termValue });
      }
    });

    // Also check for legacy single filter format
    const legacyCol = params.get(`${prefix}_search_col`);
    const legacyTerm = params.get(`${prefix}_search_term`);
    if (legacyCol && legacyTerm && !parsedFilters.some(f => f.column === legacyCol)) {
      parsedFilters.push({ column: legacyCol, term: legacyTerm });
    }

    return parsedFilters;
  }, []);

  // Helper function to update URL with multiple filters
  const updateFiltersInURL = useCallback((newFilters: any[], params: URLSearchParams, prefix: string) => {
    // Clear existing filters for this prefix
    Array.from(params.keys()).filter(key => key.startsWith(`${prefix}_filter`) || key.startsWith(`${prefix}_search`)).forEach(key => params.delete(key));

    newFilters.forEach((filter, index) => {
      params.set(`${prefix}_filter${index}Col`, filter.column);
      params.set(`${prefix}_filter${index}Term`, filter.term);
    });
  }, []);

  // Add handlers for sort actions
  const handleENO1Sort = useCallback(
    (column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
      const params = new URLSearchParams(searchParams);
      
      // Handle multiple sorts for eNO1
      let newSorts: ENO1SortConfig[] = [...eno1Sorts];
      if (direction) {
        const existingSortIndex = newSorts.findIndex(s => s.column === column);
        if (existingSortIndex >= 0) {
          if (newSorts[existingSortIndex].direction === direction) {
            newSorts.splice(existingSortIndex, 1); // Remove if same direction
          } else {
            newSorts[existingSortIndex].direction = direction; // Update direction
          }
        } else {
          newSorts.push({ column, direction }); // Add new sort
        }
      } else {
        newSorts = newSorts.filter(s => s.column !== column); // Remove sort
      }
      setEno1Sorts(newSorts);
      updateSortsInURL(newSorts, params, 'eNO1');

      // Handle multiple filters for eNO1
      let newFilters: ENO1FilterConfig[] = [...eno1Filters];
      if (searchColumn && searchTerm && searchTerm.trim()) {
        const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
        if (existingFilterIndex >= 0) {
          newFilters[existingFilterIndex].term = searchTerm.trim();
        } else {
          newFilters.push({ column: searchColumn, term: searchTerm.trim() });
        }
      } else if (searchColumn && (!searchTerm || !searchTerm.trim())) {
        newFilters = newFilters.filter((f) => f.column !== searchColumn);
      }
      setEno1Filters(newFilters);
      updateFiltersInURL(newFilters, params, 'eNO1');
      
      // Update legacy state for backward compatibility
      setEno1SortColumn(column);
      setEno1SortDirection(direction);
      setEno1SearchColumn(searchColumn);
      setEno1SearchTerm(searchTerm);
      
      navigate({ search: params.toString() });
    },
    [navigate, searchParams, eno1Sorts, eno1Filters, updateSortsInURL, updateFiltersInURL]
  );

  const handleE55Sort = useCallback(
    (column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
      const params = new URLSearchParams(searchParams);
      
      // Handle multiple sorts for e55
      let newSorts: E55SortConfig[] = [...e55Sorts];
      if (direction) {
        const existingSortIndex = newSorts.findIndex(s => s.column === column);
        if (existingSortIndex >= 0) {
          if (newSorts[existingSortIndex].direction === direction) {
            newSorts.splice(existingSortIndex, 1); // Remove if same direction
          } else {
            newSorts[existingSortIndex].direction = direction; // Update direction
          }
        } else {
          newSorts.push({ column, direction }); // Add new sort
        }
      } else {
        newSorts = newSorts.filter(s => s.column !== column); // Remove sort
      }
      setE55Sorts(newSorts);
      updateSortsInURL(newSorts, params, 'e55');

      // Handle multiple filters for e55
      let newFilters: E55FilterConfig[] = [...e55Filters];
      if (searchColumn && searchTerm && searchTerm.trim()) {
        const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
        if (existingFilterIndex >= 0) {
          newFilters[existingFilterIndex].term = searchTerm.trim();
        } else {
          newFilters.push({ column: searchColumn, term: searchTerm.trim() });
        }
      } else if (searchColumn && (!searchTerm || !searchTerm.trim())) {
        newFilters = newFilters.filter((f) => f.column !== searchColumn);
      }
      setE55Filters(newFilters);
      updateFiltersInURL(newFilters, params, 'e55');
      
      // Update legacy state for backward compatibility
      setE55SortColumn(column);
      setE55SortDirection(direction);
      setE55SearchColumn(searchColumn);
      setE55SearchTerm(searchTerm);
      
      navigate({ search: params.toString() });
    },
    [navigate, searchParams, e55Sorts, e55Filters, updateSortsInURL, updateFiltersInURL]
  );

  const handleE53Sort = useCallback(
    (column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
      const params = new URLSearchParams(searchParams);
      
      // Handle multiple sorts for e53
      let newSorts: E53SortConfig[] = [...e53Sorts];
      if (direction) {
        const existingSortIndex = newSorts.findIndex(s => s.column === column);
        if (existingSortIndex >= 0) {
          if (newSorts[existingSortIndex].direction === direction) {
            newSorts.splice(existingSortIndex, 1); // Remove if same direction
          } else {
            newSorts[existingSortIndex].direction = direction; // Update direction
          }
        } else {
          newSorts.push({ column, direction }); // Add new sort
        }
      } else {
        newSorts = newSorts.filter(s => s.column !== column); // Remove sort
      }
      setE53Sorts(newSorts);
      updateSortsInURL(newSorts, params, 'e53');

      // Handle multiple filters for e53
      let newFilters: E53FilterConfig[] = [...e53Filters];
      if (searchColumn && searchTerm && searchTerm.trim()) {
        const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
        if (existingFilterIndex >= 0) {
          newFilters[existingFilterIndex].term = searchTerm.trim();
        } else {
          newFilters.push({ column: searchColumn, term: searchTerm.trim() });
        }
      } else if (searchColumn && (!searchTerm || !searchTerm.trim())) {
        newFilters = newFilters.filter((f) => f.column !== searchColumn);
      }
      setE53Filters(newFilters);
      updateFiltersInURL(newFilters, params, 'e53');
      
      // Update legacy state for backward compatibility
      setE53SortColumn(column);
      setE53SortDirection(direction);
      setE53SearchColumn(searchColumn);
      setE53SearchTerm(searchTerm);
      
      navigate({ search: params.toString() });
    },
    [navigate, searchParams, e53Sorts, e53Filters, updateSortsInURL, updateFiltersInURL]
  );

  const handleE09Sort = useCallback(
    (column: string, direction: SortDirection | undefined, searchColumn?: string, searchTerm?: string) => {
      const params = new URLSearchParams(searchParams);
      
      // Handle multiple sorts for e09
      let newSorts: E09SortConfig[] = [...e09Sorts];
      if (direction) {
        const existingSortIndex = newSorts.findIndex(s => s.column === column);
        if (existingSortIndex >= 0) {
          if (newSorts[existingSortIndex].direction === direction) {
            newSorts.splice(existingSortIndex, 1); // Remove if same direction
          } else {
            newSorts[existingSortIndex].direction = direction; // Update direction
          }
        } else {
          newSorts.push({ column, direction }); // Add new sort
        }
      } else {
        newSorts = newSorts.filter(s => s.column !== column); // Remove sort
      }
      setE09Sorts(newSorts);
      updateSortsInURL(newSorts, params, 'e09');

      // Handle multiple filters for e09
      let newFilters: E09FilterConfig[] = [...e09Filters];
      if (searchColumn && searchTerm && searchTerm.trim()) {
        const existingFilterIndex = newFilters.findIndex((f) => f.column === searchColumn);
        if (existingFilterIndex >= 0) {
          newFilters[existingFilterIndex].term = searchTerm.trim();
        } else {
          newFilters.push({ column: searchColumn, term: searchTerm.trim() });
        }
      } else if (searchColumn && (!searchTerm || !searchTerm.trim())) {
        newFilters = newFilters.filter((f) => f.column !== searchColumn);
      }
      setE09Filters(newFilters);
      updateFiltersInURL(newFilters, params, 'e09');
      
      // Update legacy state for backward compatibility
      setE09SortColumn(column);
      setE09SortDirection(direction);
      setE09SearchColumn(searchColumn);
      setE09SearchTerm(searchTerm);
      
      navigate({ search: params.toString() });
    },
    [navigate, searchParams, e09Sorts, e09Filters, updateSortsInURL, updateFiltersInURL]
  );

  // Handle row click
  const handleRowClick = (row: PendingAction) => {
    // Navigate to the appropriate detail view based on moduleId
    const fileId = Number(row.id.split('-')[0]);
    if (row.moduleId === 'eNO1') {
      navigate(`/eno1/messages/samples/${fileId}`);
    } else if (row.moduleId === 'e55') {
      navigate(`/e55/view/${row.id}`);
    } else if (row.moduleId === 'e53') {
      navigate(`/e53/view/${row.id}`);
    } else if (row.moduleId === 'e09') {
      navigate(`/e09/view/${row.id}`);
    }
  };

  // Update rowEventHandlers to include row click
  const rowEventHandlers: RowEventHandlers<PendingAction> = {
    onContextMenuAction: handleContextMenuAction,
    onClick: handleRowClick
  };

  // Get columns for different module tables
  const getTableColumns = (moduleId: string): Column<PendingAction>[] => {
    if (moduleId === 'eNO1') {
      return getENO1Columns({ userData, tableType: 'all' });
    } else if (moduleId === 'e55') {
      return getE55Columns({ userData, tableType: 'all' });
    } else if (moduleId === 'e53') {
      return getE53Columns({ userData, tableType: 'all' });
    } else if (moduleId === 'e09') {
      return getE09Columns({ userData, tableType: 'all' });
    }

    // Default columns for other modules - this should never be reached now
    return [
      { Header: 'TODO', accessor: 'todo' },
      { Header: 'Status', accessor: 'status' },
      { Header: 'PPO', accessor: 'ppo' },
      { Header: 'Year', accessor: 'year' },
      { Header: 'Quarter', accessor: 'quarter' },
      { Header: 'Version', accessor: 'version' },
      { Header: 'Uploaded By', accessor: 'uploadedBy' },
      { Header: 'Downloads', accessor: 'downloads' },
      { Header: 'INB ACC.', accessor: 'inboundAcceptance' },
      { Header: 'Outb. ACC.', accessor: 'outboundAcceptance' },
      { Header: 'Samples', accessor: 'samples' },
      { Header: 'Filename', accessor: 'filename' },
      { Header: 'Comments', accessor: 'comments' },
      { Header: 'Removal DO', accessor: 'removalDo' }
    ];
  };

  // Get filter values from URL query parameters - memoized to ensure hooks refetch when params change
  const filtersFromUrl = useMemo(() => {
    const state = searchParams.get('state') || 'all'; // Changed from 'status' to 'state'
    const typeParams = searchParams.getAll('type');
    const type = typeParams.length > 0 ? typeParams : ['all'];
    const view = searchParams.get('view') || 'Grid';
    const yearParams = searchParams.getAll('year');
    const years = yearParams.length > 0 ? yearParams : ['all'];
    const ppo = searchParams.get('ppo') || 'all';
    
    const hasAnyFilter = searchParams.toString().length > 0;
    
    return {
      state, // Changed from status to state
      type,
      view,
      years,
      ppo,
      hasAnyFilter
    };
  }, [searchParams]);

  // Keep getFiltersFromUrl for backward compatibility
  const getFiltersFromUrl = useCallback(() => filtersFromUrl, [filtersFromUrl]);
  
  // State to track which tables are visible - initialize based on current URL params
  const [visibleTables, setVisibleTables] = useState(() => {
    // Check if there are any search params on initial load
    const hasParams = searchParams.toString().length > 0;
    
    // If no params, show all tables
    if (!hasParams) {
      return {
        pending: true,
        outbound: true,
        inbound: true
      };
    }
    
    // Otherwise, determine initial visibility based on current type param
    const typeParams = searchParams.getAll('type');
    const showAll = typeParams.length === 0 || typeParams.includes('all');
    
    return {
      pending: showAll,
      outbound: showAll || typeParams.includes('outbound'),
      inbound: showAll || typeParams.includes('inbound')
    };
  });
  
  // Update table visibility when URL parameters change
  useEffect(() => {
    // If there are no filters in the URL, show all tables
    if (!filtersFromUrl.hasAnyFilter) {
      setVisibleTables({
        pending: true,
        outbound: true,
        inbound: true
      });
      return;
    }
    
    const typeFilter = Array.isArray(filtersFromUrl.type) 
      ? filtersFromUrl.type 
      : [filtersFromUrl.type];
    
    
    const showAll = typeFilter.includes('all') || typeFilter.length === 0;
    
    // Only show the specific tables when a type filter is selected, 
    // or show all tables when "all" is selected
    setVisibleTables({
      // Only show pending actions when "all" is selected
      pending: showAll,
      // Show outbound table when "all" is selected or "outbound" is specifically selected
      outbound: showAll || typeFilter.includes('outbound'),
      // Show inbound table when "all" is selected or "inbound" is specifically selected
      inbound: showAll || typeFilter.includes('inbound')
    });
    
  }, [filtersFromUrl]); // Run when filtersFromUrl change

  // Close sidebar on mobile by default
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [isMobile]);



  const { data: reports, isLoading: isReportsLoading } = useQuery<Report[]>({
    queryKey: ['reports'],
    queryFn: fetchReports,
    retry: 3,
    retryDelay: 1000,
    staleTime: 60000, // 1 minute
  });

  const { data: outboundReports, isLoading: isOutboundReportsLoading } = useQuery<Report[]>({
    queryKey: ['outboundReports'],
    queryFn: fetchOutboundReports,
    retry: 3,
    retryDelay: 1000,
    staleTime: 60000, // 1 minute
  });

  const { data: inboundReports, isLoading: isInboundReportsLoading } = useQuery<Report[]>({
    queryKey: ['inboundReports'],
    queryFn: fetchInboundReports,
    retry: 3,
    retryDelay: 1000,
    staleTime: 60000, // 1 minute
  });

  // Filter reports based on status, type, year AND ppo from URL
  const filteredReports = useMemo(() => {
    if (!reports) return [];
    
    // If no filters are applied, return all reports
    if (!filtersFromUrl.hasAnyFilter) {
      return reports;
    }
    
    const state = filtersFromUrl.state;
    const typeFilter = Array.isArray(filtersFromUrl.type) 
      ? filtersFromUrl.type 
      : [filtersFromUrl.type];
    const yearFilters = Array.isArray(filtersFromUrl.years) 
      ? filtersFromUrl.years 
      : [filtersFromUrl.years];
    const ppoFilter = filtersFromUrl.ppo;
    
    // If a specific type is selected (not 'all'), don't show pending actions
    if (!typeFilter.includes('all') && typeFilter.length > 0) {
      return []; // Return empty array to hide pending actions when specific type is selected
    }
    
    // Create a base filtered array by status
    let filtered = reports;
    
    // Apply status filter
    if (state !== 'all') {
      filtered = filtered.filter(report => {
        if (state === 'pending') return report.status === 'pending';
        if (state === 'completed') return report.status === 'completed';
        if (state === 'failed') return report.status === 'failed';
        return true;
      });
    }
    
    // Apply year filter - show if any of the selected years match
    if (!yearFilters.includes('all')) {
      filtered = filtered.filter(report => 
        yearFilters.includes(report.year.toString())
      );
    }
    
    // Apply PPO filter
    if (ppoFilter !== 'all') {
      filtered = filtered.filter(report => 
        report.ppo === ppoFilter
      );
    }
    
    return filtered;
  }, [reports, filtersFromUrl]); // Depend on filtersFromUrl

  // Filter outbound reports based on status, year and PPO from URL
  const filteredOutboundReports = useMemo(() => {
    if (!outboundReports) return [];
    
    // If no filters are applied, return all outbound reports
    if (!filtersFromUrl.hasAnyFilter) {
      return outboundReports;
    }
    
    const state = filtersFromUrl.state;
    const typeFilter = Array.isArray(filtersFromUrl.type) 
      ? filtersFromUrl.type 
      : [filtersFromUrl.type];
    const yearFilters = Array.isArray(filtersFromUrl.years) 
      ? filtersFromUrl.years 
      : [filtersFromUrl.years];
    const ppoFilter = filtersFromUrl.ppo;
    
    // If "inbound" is specifically selected (without "all"), don't show outbound reports
    if (typeFilter.includes('inbound') && !typeFilter.includes('all') && !typeFilter.includes('outbound')) {
      return []; // Return empty array to hide outbound reports when only inbound is selected
    }
    
    // Create a base filtered array
    let filtered = outboundReports;
    
    // Apply status filter
    if (state !== 'all') {
      filtered = filtered.filter(report => {
        if (state === 'pending') return report.status === 'pending';
        if (state === 'completed') return report.status === 'completed';
        if (state === 'failed') return report.status === 'failed';
        return true;
      });
    }
    
    // Apply year filter - show if any of the selected years match
    if (!yearFilters.includes('all')) {
      filtered = filtered.filter(report => 
        yearFilters.includes(report.year.toString())
      );
    }
    
    // Apply PPO filter
    if (ppoFilter !== 'all') {
      filtered = filtered.filter(report => 
        report.ppo === ppoFilter
      );
    }
    
    return filtered;
  }, [outboundReports, filtersFromUrl]); // Depend on filtersFromUrl

  // Filter inbound reports based on status, year and PPO from URL
  const filteredInboundReports = useMemo(() => {
    if (!inboundReports) return [];
    
    // If no filters are applied, return all inbound reports
    if (!filtersFromUrl.hasAnyFilter) {
      return inboundReports;
    }
    
    const state = filtersFromUrl.state;
    const typeFilter = Array.isArray(filtersFromUrl.type) 
      ? filtersFromUrl.type 
      : [filtersFromUrl.type];
    const yearFilters = Array.isArray(filtersFromUrl.years) 
      ? filtersFromUrl.years 
      : [filtersFromUrl.years];
    const ppoFilter = filtersFromUrl.ppo;
    
    // If "outbound" is specifically selected (without "all"), don't show inbound reports
    if (typeFilter.includes('outbound') && !typeFilter.includes('all') && !typeFilter.includes('inbound')) {
      return []; // Return empty array to hide inbound reports when only outbound is selected
    }
    
    // Create a base filtered array
    let filtered = inboundReports;
    
    // Apply status filter
    if (state !== 'all') {
      filtered = filtered.filter(report => {
        if (state === 'pending') return report.status === 'pending';
        if (state === 'completed') return report.status === 'completed';
        if (state === 'failed') return report.status === 'failed';
        return true;
      });
    }
    
    // Apply year filter - show if any of the selected years match
    if (!yearFilters.includes('all')) {
      filtered = filtered.filter(report => 
        yearFilters.includes(report.year.toString())
      );
    }
    
    // Apply PPO filter
    if (ppoFilter !== 'all') {
      filtered = filtered.filter(report => 
        report.ppo === ppoFilter
      );
    }
    
    return filtered;
  }, [inboundReports, filtersFromUrl]); // Depend on filtersFromUrl

  // Transform modules to NavItems
  const navItems = useNavItems();

  // Create module-specific context menu actions
  const getModuleContextMenuActions = useCallback((moduleId: string) => {
    // Define view and edit actions based on module type
    const getViewEditActions = (moduleId: string) => {
      if (moduleId === 'eNO1' || moduleId === 'e53') {
        return [
          { 
            action: 'ViewSamples' as ContextMenuAction, 
            label: 'View Samples', 
            icon: <Visibility /> 
          },
          { 
            action: 'editSamples' as ContextMenuAction, 
            label: 'Edit Samples', 
            icon: <Edit /> 
          },
        ];
      } else if (moduleId === 'e09' || moduleId === 'e55') {
        return [
          { 
            action: 'ViewSamples' as ContextMenuAction, 
            label: 'View Despatches', 
            icon: <Visibility /> 
          },
          { 
            action: 'editSamples' as ContextMenuAction, 
            label: 'Edit Despatches', 
            icon: <Edit /> 
          },
        ];
      }
      // Default fallback
      return [
        { 
          action: 'ViewSamples' as ContextMenuAction, 
          label: 'View Samples', 
          icon: <Visibility /> 
        },
        { 
          action: 'editSamples' as ContextMenuAction, 
          label: 'Edit Samples', 
          icon: <Edit /> 
        },
      ];
    };

    const viewEditActions = getViewEditActions(moduleId);
    
    const baseActions = [
      { 
        action: 'download' as ContextMenuAction, 
        label: 'Download', 
        icon: <CloudDownload /> 
      },
      { 
        action: 'accept' as ContextMenuAction, 
        label: 'Accept', 
        icon: <Check /> 
      },
      { 
        action: 'delete' as ContextMenuAction, 
        label: 'Delete', 
        icon: <Delete /> 
      },
      ...viewEditActions,
      { 
        action: 'comment' as ContextMenuAction, 
        label: 'Comment', 
        icon: <Comment /> 
      },
      { 
        action: 'contact' as ContextMenuAction, 
        label: 'Contact', 
        icon: <Email /> 
      },
      { 
        action: 'more' as ContextMenuAction, 
        label: 'More', 
        icon: <Help /> 
      },
    ];

    // Add module-specific report actions
    switch (moduleId) {
      case 'eNO1':
        return [
          ...baseActions.slice(0, 5), // download, accept, delete, ViewSamples, editSamples
          { 
            action: 'no2Report' as ContextMenuAction, 
            label: 'NO2 Report', 
            icon: <BarChart /> 
          },
          { 
            action: 'no3Report' as ContextMenuAction, 
            label: 'NO3 Report', 
            icon: <BarChart /> 
          },
          ...baseActions.slice(5) // comment, contact, more
        ];
      
      case 'e53':
        return [
          ...baseActions.slice(0, 5), // download, accept, delete, ViewSamples, editSamples
          { 
            action: 'cn54Report' as ContextMenuAction, 
            label: 'CN54 Report', 
            icon: <BarChart /> 
          },
          ...baseActions.slice(5) // comment, contact, more
        ];
      
      case 'e55':
        return [
          ...baseActions.slice(0, 5), // download, accept, delete, ViewSamples, editSamples
          { 
            action: 'cn56Report' as ContextMenuAction, 
            label: 'CN56 Report', 
            icon: <BarChart /> 
          },
          ...baseActions.slice(5) // comment, contact, more
        ];
      
      case 'e09':
        return [
          ...baseActions.slice(0, 5), // download, accept, delete, ViewSamples, editSamples
          { 
            action: 'cn10Report' as ContextMenuAction, 
            label: 'CN10 Report', 
            icon: <BarChart /> 
          },
          { 
            action: 'cn19Report' as ContextMenuAction, 
            label: 'CN19 Report', 
            icon: <BarChart /> 
          },
          ...baseActions.slice(5) // comment, contact, more
        ];
      
      default:
        return baseActions;
    }
  }, []);

  // Log errors for debugging
  useEffect(() => {
    if (userError) {
      console.error('User info query error:', userError);
    }
  }, [userError]);

  // Retry fetching user info if it fails
  useEffect(() => {
    if (!userData && !isUserLoading) {
      const retryTimer = setTimeout(() => {
        refetchUser();
      }, 5000);
      
      return () => clearTimeout(retryTimer);
    }
  }, [userData, isUserLoading, refetchUser]);

  // Get pagination values from URL for each table
  const getTablePagination = (tableId: string) => {
    const page = parseInt(searchParams.get(`${tableId}_page`) || '0');
    const size = parseInt(searchParams.get(`${tableId}_size`) || '10');
    return { page, size };
  };

  // Handle pagination change for specific table
  const handlePaginationChange = (tableId: string, newPage: number, newSize: number): void => {
    const params = new URLSearchParams(searchParams);
    params.set(`${tableId}_page`, newPage.toString());
    params.set(`${tableId}_size`, newSize.toString());
    navigate({ search: params.toString() });
  };

  // Get designatedOperator from PPO filter - convert 'all' or 'ALL' to empty string
  const designatedOperator = useMemo(() => {
    const ppo = filtersFromUrl.ppo?.toUpperCase();
    return (ppo === 'ALL' || ppo === 'all' || !ppo) ? '' : filtersFromUrl.ppo;
  }, [filtersFromUrl.ppo]);

  // Fetch data from all modules with pagination and sorting
  const {
    data: eNO1Data,
    isLoading: isENO1Loading,
    isError: isENO1Error
  } = useENO1Data({
    ...getTablePagination('eNO1'),
    table: 'pending',
    designatedOperator,
    state: filtersFromUrl.state === 'all' ? 'all' : filtersFromUrl.state as 'pending' | 'completed',
    accountingYear: filtersFromUrl.years[0] === 'all' ? undefined : filtersFromUrl.years[0],
    sort: eno1SortColumn && eno1SortDirection && eno1SortDirection !== 'undefined' as any ? `${eno1SortColumn},${eno1SortDirection}` : undefined,
    searchColumn: eno1SearchColumn,
    searchTerm: eno1SearchTerm
  });

  const {
    data: e55Data,
    isLoading: isE55Loading,
    isError: isE55Error
  } = useE55Data({
    ...getTablePagination('e55'),
    table: 'pending',
    designatedOperator,
    state: filtersFromUrl.state === 'all' ? 'all' : filtersFromUrl.state as 'pending' | 'completed',
    accountingYear: filtersFromUrl.years[0] === 'all' ? undefined : filtersFromUrl.years[0],
    sorts: e55Sorts.length > 0 ? e55Sorts : undefined,
    filters: e55Filters.length > 0 ? e55Filters : undefined,
    sort: e55SortColumn && e55SortDirection && e55SortDirection !== 'undefined' as any ? `${e55SortColumn},${e55SortDirection}` : undefined,
    searchColumn: e55SearchColumn,
    searchTerm: e55SearchTerm
  });

  const {
    data: e53Data,
    isLoading: isE53Loading,
    isError: isE53Error
  } = useE53Data({
    ...getTablePagination('e53'),
    table: 'pending',
    designatedOperator,
    state: filtersFromUrl.state === 'all' ? 'all' : filtersFromUrl.state as 'pending' | 'completed',
    accountingYear: filtersFromUrl.years[0] === 'all' ? undefined : filtersFromUrl.years[0],
    sorts: e53Sorts.length > 0 ? e53Sorts : undefined,
    filters: e53Filters.length > 0 ? e53Filters : undefined,
    searchColumn: e53SearchColumn,
    searchTerm: e53SearchTerm
  });

  const {
    data: e09Data,
    isLoading: isE09Loading,
    isError: isE09Error
  } = useE09Data({
    ...getTablePagination('e09'),
    table: 'pending',
    designatedOperator,
    state: filtersFromUrl.state === 'all' ? 'all' : filtersFromUrl.state as 'pending' | 'completed',
    accountingYear: filtersFromUrl.years[0] === 'all' ? undefined : filtersFromUrl.years[0],
    sorts: e09Sorts.length > 0 ? e09Sorts : undefined,
    filters: e09Filters.length > 0 ? e09Filters : undefined,
    sort: e09SortColumn && e09SortDirection && e09SortDirection !== 'undefined' as any ? `${e09SortColumn},${e09SortDirection}` : undefined,
    searchColumn: e09SearchColumn,
    searchTerm: e09SearchTerm
  });

  // Transform module data to PendingActions
  const transformModuleDataToPendingActions = (data: any[], moduleId: string): PendingAction[] => {
    if (!data) return [];

    return data.map((item, index) => {
      // Determine PPO value - use operatorByInboundPpo as default, or operatorByOutboundPpo if inbound matches user's operator
      // For display/filtering purposes, we'll use operatorByInboundPpo as the primary PPO
      const ppoValue = item.operatorByInboundPpo || item.operatorByOutboundPpo || '';
      
      return {
        id: `${item.fileId}-${ppoValue}-${item.accountingYear}-${item.accountingQuarter}-${item.fileVersion}-${index}-${Math.random().toString(36).substring(2, 9)}`,
        moduleId,
        status: item.fileStatus,
        ppo: ppoValue,
        year: item.accountingYear,
        quarter: Number(item.accountingQuarter),
        version: item.fileVersion,
        uploadedBy: item.operatorByFileUploader || 'Unknown',
        downloads: Number(item.downloadDate ? 1 : 0),
        inboundAcceptance: item.inboundAcceptanceDate ? new Date(item.inboundAcceptanceDate).toLocaleDateString('en-GB') : '-',
        outboundAcceptance: item.outboundAcceptanceDate ? new Date(item.outboundAcceptanceDate).toLocaleDateString('en-GB') : '-',
        samples: item.samples || 0,
        filename: item.fileName,
        comment: item.comment,
        removalDo: item.removalDo || '',
        pendingOn: item.pendingOn || null,
        todo: 'Review',
        priority: 'medium',
        createdAt: new Date().toISOString(),
        
        // Add original fields to match ENO1Message expected fields
        fileId: item.fileId,
        fileStatus: item.fileStatus,
        accountingYear: item.accountingYear,
        accountingQuarter: item.accountingQuarter,
        fileVersion: item.fileVersion,
        operatorByFileUploader: item.operatorByFileUploader,
        downloadDate: item.downloadDate,
        inboundAcceptanceDate: item.inboundAcceptanceDate,
        outboundAcceptanceDate: item.outboundAcceptanceDate,
        sampleCount: item.samples,
        fileName: item.fileName,
        removalPpo: item.removalDo,
        removalDate: item.removalDate,
        operatorByOutboundPpo: item.operatorByOutboundPpo,
        operatorByInboundPpo: item.operatorByInboundPpo
      };
    });
  };

  // Combine all pending actions
  const allPendingActions = useMemo(() => {
    const eNO1Actions = transformModuleDataToPendingActions(eNO1Data?.content || [], 'eNO1');
    const e55Actions = transformModuleDataToPendingActions(e55Data?.content || [], 'e55');
    const e53Actions = transformModuleDataToPendingActions(e53Data?.content || [], 'e53');
    const e09Actions = transformModuleDataToPendingActions(e09Data?.content || [], 'e09');

    return [...eNO1Actions, ...e55Actions, ...e53Actions, ...e09Actions];
  }, [eNO1Data, e55Data, e53Data, e09Data]);

  // Filter pending actions based on URL parameters
  const filteredPendingActions = useMemo(() => {
    let filtered = [...allPendingActions];

    // Apply status filter
    if (filtersFromUrl.state !== 'all') {
      filtered = filtered.filter(action => {
        if (filtersFromUrl.state === 'pending') return action.status === 'D';
        if (filtersFromUrl.state === 'completed') return action.status === 'V';
        if (filtersFromUrl.state === 'failed') return action.status === 'F';
        return true;
      });
    }

    // Apply year filter
    if (!filtersFromUrl.years.includes('all')) {
      filtered = filtered.filter(action => 
        filtersFromUrl.years.includes(action.year.toString())
      );
    }

    // Apply PPO filter - check both inbound and outbound PPOs
    if (filtersFromUrl.ppo !== 'all' && filtersFromUrl.ppo !== 'ALL') {
      filtered = filtered.filter(action => 
        action.ppo === filtersFromUrl.ppo ||
        action.operatorByInboundPpo === filtersFromUrl.ppo ||
        action.operatorByOutboundPpo === filtersFromUrl.ppo
      );
    }

    return filtered;
  }, [allPendingActions, filtersFromUrl]);

  // Extract sort parameters from URL on component mount
  useEffect(() => {
    const eno1SortColParam = searchParams.get('eNO1_sort_col');
    const eno1SortDirParam = searchParams.get('eNO1_sort_dir') as SortDirection | null;
    const eno1SearchColParam = searchParams.get('eNO1_search_col');
    const eno1SearchTermParam = searchParams.get('eNO1_search_term');
    const e55SortColParam = searchParams.get('e55_sort_col');
    const e55SortDirParam = searchParams.get('e55_sort_dir') as SortDirection | null;
    const e55SearchColParam = searchParams.get('e55_search_col');
    const e55SearchTermParam = searchParams.get('e55_search_term');
    const e53SortColParam = searchParams.get('e53_sort_col');
    const e53SortDirParam = searchParams.get('e53_sort_dir') as SortDirection | null;
    const e53SearchColParam = searchParams.get('e53_search_col');
    const e53SearchTermParam = searchParams.get('e53_search_term');
    const e09SortColParam = searchParams.get('e09_sort_col');
    const e09SortDirParam = searchParams.get('e09_sort_dir') as SortDirection | null;
    const e09SearchColParam = searchParams.get('e09_search_col');
    const e09SearchTermParam = searchParams.get('e09_search_term');

    if (eno1SortColParam) setEno1SortColumn(eno1SortColParam);
    if (eno1SortDirParam) setEno1SortDirection(eno1SortDirParam);
    if (eno1SearchColParam) setEno1SearchColumn(eno1SearchColParam);
    if (eno1SearchTermParam) setEno1SearchTerm(eno1SearchTermParam);
    if (e55SortColParam) setE55SortColumn(e55SortColParam);
    if (e55SortDirParam) setE55SortDirection(e55SortDirParam);
    if (e55SearchColParam) setE55SearchColumn(e55SearchColParam);
    if (e55SearchTermParam) setE55SearchTerm(e55SearchTermParam);
    if (e53SortColParam) setE53SortColumn(e53SortColParam);
    if (e53SortDirParam) setE53SortDirection(e53SortDirParam);
    if (e53SearchColParam) setE53SearchColumn(e53SearchColParam);
    if (e53SearchTermParam) setE53SearchTerm(e53SearchTermParam);
    if (e09SortColParam) setE09SortColumn(e09SortColParam);
    if (e09SortDirParam) setE09SortDirection(e09SortDirParam);
    if (e09SearchColParam) setE09SearchColumn(e09SearchColParam);
    if (e09SearchTermParam) setE09SearchTerm(e09SearchTermParam);
  }, [searchParams]);

  // Add submit comment hooks for each module with appropriate handlers
  const eno1SubmitComment = useENO1SubmitComment({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Comment submitted successfully',
        severity: 'success',
      });
      handleCloseCommentWithTableModal();
      
      // Refetch data by triggering a page reload to get updated data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Failed to submit comment: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    },
  });

  const e55SubmitComment = useE55SubmitComment({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Comment submitted successfully',
        severity: 'success',
      });
      handleCloseCommentWithTableModal();
      
      // Refetch data by triggering a page reload to get updated data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Failed to submit comment: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    },
  });

  const e53SubmitComment = useE53SubmitComment({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Comment submitted successfully',
        severity: 'success',
      });
      handleCloseCommentWithTableModal();
      
      // Refetch data by triggering a page reload to get updated data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Failed to submit comment: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    },
  });

  const e09SubmitComment = useE09SubmitComment({
    onSuccess: () => {
      setSnackbar({
        open: true,
        message: 'Comment submitted successfully',
        severity: 'success',
      });
      handleCloseCommentWithTableModal();
      
      // Refetch data by triggering a page reload to get updated data
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Failed to submit comment: ${error.message || 'Unknown error'}`,
        severity: 'error',
      });
    },
  });

  // Fetch comments based on the selected module and file
  const { data: eno1CommentsData, isLoading: isEno1CommentsLoading } = useENO1Comments({
    dataSetId: commentWithTableModal.fileId || 0,
    enabled: commentWithTableModal.isOpen && commentWithTableModal.moduleId === 'eNO1' && !!commentWithTableModal.fileId,
    page: commentPagination.page,
    size: commentPagination.size
  });

  const { data: e55CommentsData, isLoading: isE55CommentsLoading } = useE55Comments({
    dataSetId: commentWithTableModal.fileId || 0,
    enabled: commentWithTableModal.isOpen && commentWithTableModal.moduleId === 'e55' && !!commentWithTableModal.fileId,
    page: commentPagination.page,
    size: commentPagination.size
  });

  const { data: e53CommentsData, isLoading: isE53CommentsLoading } = useE53Comments({
    dataSetId: commentWithTableModal.fileId || 0,
    enabled: commentWithTableModal.isOpen && commentWithTableModal.moduleId === 'e53' && !!commentWithTableModal.fileId,
    page: commentPagination.page,
    size: commentPagination.size
  });

  const { data: e09CommentsData, isLoading: isE09CommentsLoading } = useE09Comments({
    dataSetId: commentWithTableModal.fileId || 0,
    enabled: commentWithTableModal.isOpen && commentWithTableModal.moduleId === 'e09' && !!commentWithTableModal.fileId,
    page: commentPagination.page,
    size: commentPagination.size
  });

  if (userError) {
    return (
      <Box sx={{ p: 3 }} data-testid="home-error">
        <Alert severity="error" data-testid="home-error-alert">
          Error loading data. Please try again later.
        </Alert>
      </Box>
    );
  }

  if (isUserLoading || isReportsLoading || isOutboundReportsLoading || isInboundReportsLoading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh' 
        }}
        data-testid="home-loading"
      >
        <CircularProgress data-testid="home-loading-spinner" />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid="home-container">
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title="Home"
            onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            isSidebarOpen={sidebarOpen}
            showFilters={{
              status: false,
              type: false,
              year: true,
              ppo: true
            }}
            data-testid="home-section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar 
                isOpen={sidebarOpen} 
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                navItems={navItems}
                data-testid="home-sidebar"
              />
              {isMobile && sidebarOpen && (
                <Backdrop
                  open={true}
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  sx={{ zIndex: 1100, backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
                  data-testid="home-mobile-backdrop"
                />
              )}
            </Box>
          )}

          {/* MainContent */}
          <MainContent sidebarOpen={sidebarOpen} data-testid="home-main-content">
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: 2, 
              width: '100%', 
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              {/* eNO1 Table - Only show if user has access to eNO1 module */}
              {hasModuleAccess('eNO1') && (
                <Box component={Paper} sx={{ mb: 2 }} data-testid="home-eno1-table-container">
                                     <Table
                     data={filteredPendingActions.filter(action => action.moduleId === 'eNO1')}
                     moduleName="eNO1 Sampling"
                     columns={getTableColumns('eNO1')}
                     page={getTablePagination('eNO1').page}
                     pageSize={getTablePagination('eNO1').size}
                     onPageChange={(newPage) => handlePaginationChange('eNO1', newPage, getTablePagination('eNO1').size)}
                     onPageSizeChange={(newSize) => handlePaginationChange('eNO1', 0, newSize)}
                     totalCount={eNO1Data?.totalElements || 0}
                     rowEventHandlers={rowEventHandlers}
                     hasActions={true}
                     customActions={getModuleContextMenuActions('eNO1')}
                     hideDefaultActions={true}
                     onSort={handleENO1Sort}
                     todoColumn={true}
                     rowBackgroundColor={getRowBackgroundColor}
                     defaultSort={eno1Sorts.length > 0 ? eno1Sorts[0] : (eno1SortColumn && eno1SortDirection ? { column: eno1SortColumn, direction: eno1SortDirection } : undefined)}
                     defaultSorts={eno1Sorts.length > 0 ? eno1Sorts : undefined}
                     defaultFilters={eno1Filters.length > 0 ? eno1Filters : undefined}
                     initialSearchColumn={eno1SearchColumn}
                     initialSearchTerm={eno1SearchTerm}
                     data-testid="home-eno1-table"
                   />
                </Box>
              )}

               {/* e53 Table - Only show if user has access to e53 module */}
               {hasModuleAccess('e53') && (
                <Box component={Paper} sx={{ mb: 2 }} data-testid="home-e53-table-container">
                                     <Table
                     data={filteredPendingActions.filter(action => action.moduleId === 'e53')}
                     moduleName="e53 Sampling"
                     columns={getTableColumns('e53')}
                     page={getTablePagination('e53').page}
                     pageSize={getTablePagination('e53').size}
                     onPageChange={(newPage) => handlePaginationChange('e53', newPage, getTablePagination('e53').size)}
                     onPageSizeChange={(newSize) => handlePaginationChange('e53', 0, newSize)}
                     totalCount={e53Data?.totalElements || 0}
                     rowEventHandlers={rowEventHandlers}
                     hasActions={true}
                     customActions={getModuleContextMenuActions('e53')}
                     hideDefaultActions={true}
                     todoColumn={true}
                     rowBackgroundColor={getRowBackgroundColor}
                     onSort={handleE53Sort}
                     defaultSort={e53Sorts.length > 0 ? e53Sorts[0] : (e53SortColumn && e53SortDirection ? { column: e53SortColumn, direction: e53SortDirection } : undefined)}
                     defaultSorts={e53Sorts.length > 0 ? e53Sorts : undefined}
                     defaultFilters={e53Filters.length > 0 ? e53Filters : undefined}
                     initialSearchColumn={e53SearchColumn}
                     initialSearchTerm={e53SearchTerm}
                     data-testid="home-e53-table"
                   />
                </Box>
              )}

              {/* e55 Table - Only show if user has access to e55 module */}
              {hasModuleAccess('e55') && (
                <Box component={Paper} sx={{ mb: 2 }} data-testid="home-e55-table-container">
                                     <Table
                     data={filteredPendingActions.filter(action => action.moduleId === 'e55')}
                     moduleName="e55 Weights"
                     columns={getTableColumns('e55')}
                     page={getTablePagination('e55').page}
                     pageSize={getTablePagination('e55').size}
                     onPageChange={(newPage) => handlePaginationChange('e55', newPage, getTablePagination('e55').size)}
                     onPageSizeChange={(newSize) => handlePaginationChange('e55', 0, newSize)}
                     totalCount={e55Data?.totalElements || 0}
                     rowEventHandlers={rowEventHandlers}
                     hasActions={true}
                     customActions={getModuleContextMenuActions('e55')}
                     todoColumn={true}
                     hideDefaultActions={true}
                     rowBackgroundColor={getRowBackgroundColor}
                     onSort={handleE55Sort}
                     defaultSort={e55Sorts.length > 0 ? e55Sorts[0] : (e55SortColumn && e55SortDirection ? { column: e55SortColumn, direction: e55SortDirection } : undefined)}
                     defaultSorts={e55Sorts.length > 0 ? e55Sorts : undefined}
                     defaultFilters={e55Filters.length > 0 ? e55Filters : undefined}
                     initialSearchColumn={e55SearchColumn}
                     initialSearchTerm={e55SearchTerm}
                     data-testid="home-e55-table"
                   />
                </Box>
              )}

              {/* e09 Table - Only show if user has access to e09 module */}
              {hasModuleAccess('e09') && (
                <Box component={Paper} sx={{ mb: 2 }} data-testid="home-e09-table-container">
                                     <Table
                     data={filteredPendingActions.filter(action => action.moduleId === 'e09')}
                     moduleName="e09 IBRS"
                     columns={getTableColumns('e09')}
                     page={getTablePagination('e09').page}
                     pageSize={getTablePagination('e09').size}
                     onPageChange={(newPage) => handlePaginationChange('e09', newPage, getTablePagination('e09').size)}
                     onPageSizeChange={(newSize) => handlePaginationChange('e09', 0, newSize)}
                     totalCount={e09Data?.totalElements || 0}
                     rowEventHandlers={rowEventHandlers}
                     hasActions={true}
                     todoColumn={true}
                     customActions={getModuleContextMenuActions('e09')}
                     hideDefaultActions={true}
                     rowBackgroundColor={getRowBackgroundColor}
                     onSort={handleE09Sort}
                     defaultSort={e09Sorts.length > 0 ? e09Sorts[0] : (e09SortColumn && e09SortDirection ? { column: e09SortColumn, direction: e09SortDirection } : undefined)}
                     defaultSorts={e09Sorts.length > 0 ? e09Sorts : undefined}
                     defaultFilters={e09Filters.length > 0 ? e09Filters : undefined}
                     initialSearchColumn={e09SearchColumn}
                     initialSearchTerm={e09SearchTerm}
                     data-testid="home-e09-table"
                   />
                </Box>
              )}
            </Box>
            
            {/* Notification Snackbar */}
            <NotificationSnackbar
              open={snackbar.open}
              message={snackbar.message}
              severity={snackbar.severity}
              onClose={handleSnackbarClose}
              data-testid="home-snackbar"
            />
          </MainContent>
        </Grid>
      </Grid>

      {/* Comment Modal for Deletion - Updated to be more generic */}
      <CommentModal
        isOpen={commentModal.isOpen}
        onClose={handleCloseCommentModal}
        onSubmit={handleCommentSubmit}
        title={`Delete ${commentModal.moduleId} File`}
        description="Please provide a comment explaining why you are deleting this file."
        submitText="Delete"
        cancelText="Cancel"
        data-testid="home-comment-modal"
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.isOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        data-testid="home-confirmation-dialog"
      >
        <DialogTitle id="alert-dialog-title">{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary" data-testid="home-dialog-cancel-button">
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} color="primary" autoFocus data-testid="home-dialog-confirm-button">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* CommentWithTable Modal */}
      <CommentWithTable
        isOpen={commentWithTableModal.isOpen}
        onClose={handleCloseCommentWithTableModal}
        onSubmit={handleCommentWithTableSubmit}
        title={`Comment on File ${commentWithTableModal.fileId || ''}`}
        submitText='Submit Comment'
        cancelText='Cancel'
        tableData={
          commentWithTableModal.moduleId === 'eNO1' ? eno1CommentsData?.comments || [] :
          commentWithTableModal.moduleId === 'e55' ? e55CommentsData?.comments || [] :
          commentWithTableModal.moduleId === 'e53' ? e53CommentsData?.comments || [] :
          commentWithTableModal.moduleId === 'e09' ? e09CommentsData?.comments || [] :
          []
        }
        datasetInfo={
          commentWithTableModal.moduleId === 'eNO1' && eno1CommentsData ? {
            accountingYear: eno1CommentsData.accountingYear,
            accountingQuarter: eno1CommentsData.accountingQuarter,
            inboundRemovalDate: eno1CommentsData.inboundRemovalDate,
            outboundRemovalDate: eno1CommentsData.outboundRemovalDate,
          } :
          commentWithTableModal.moduleId === 'e55' && e55CommentsData ? {
            accountingYear: e55CommentsData.accountingYear,
            accountingQuarter: e55CommentsData.accountingQuarter,
            inboundRemovalDate: e55CommentsData.inboundRemovalDate,
            outboundRemovalDate: e55CommentsData.outboundRemovalDate,
          } :
          commentWithTableModal.moduleId === 'e53' && e53CommentsData ? {
            accountingYear: e53CommentsData.accountingYear,
            accountingQuarter: e53CommentsData.accountingQuarter,
            inboundRemovalDate: e53CommentsData.inboundRemovalDate,
            outboundRemovalDate: e53CommentsData.outboundRemovalDate,
          } :
          commentWithTableModal.moduleId === 'e09' && e09CommentsData ? {
            accountingYear: e09CommentsData.accountingYear,
            accountingQuarter: e09CommentsData.accountingQuarter,
            inboundRemovalDate: e09CommentsData.inboundRemovalDate,
            outboundRemovalDate: e09CommentsData.outboundRemovalDate,
          } :
          undefined
        }
        columns={[
          {
            Header: 'Date',
            accessor: 'date',
            width: '20%',
          },
          {
            Header: 'DO',
            accessor: 'ppo',
            width: '20%',
          },
          {
            Header: 'Version',
            accessor: 'version',
            width: '20%',
          },
          {
            Header: 'Comment',
            accessor: 'comment',
            width: '39%',
          }
        ]}
        commentRequired={true}
        commentLabel='Your Comment'
        commentPlaceholder='Enter your comment here...'
        maxWidth="lg"
        fullWidth={true}
        // Server-side pagination props
        pageSize={commentPagination.size}
        totalCount={
          commentWithTableModal.moduleId === 'eNO1' ? eno1CommentsData?.totalElements || 0 :
          commentWithTableModal.moduleId === 'e55' ? e55CommentsData?.totalElements || 0 :
          commentWithTableModal.moduleId === 'e53' ? e53CommentsData?.totalElements || 0 :
          commentWithTableModal.moduleId === 'e09' ? e09CommentsData?.totalElements || 0 :
          0
        }
        currentPage={commentPagination.page}
        onPageChange={handleCommentPageChange}
        onPageSizeChange={handleCommentPageSizeChange}
        isLoading={
          commentWithTableModal.moduleId === 'eNO1' ? isEno1CommentsLoading :
          commentWithTableModal.moduleId === 'e55' ? isE55CommentsLoading :
          commentWithTableModal.moduleId === 'e53' ? isE53CommentsLoading :
          commentWithTableModal.moduleId === 'e09' ? isE09CommentsLoading :
          false
        }
        data-testid="home-comment-with-table-modal"
      />

      {/* Contact Modal */}
      <ContactModal
        isOpen={contactModal.isOpen}
        onClose={() => setContactModal({ isOpen: false, selectedRow: null, moduleId: null })}
        row={contactModal.selectedRow}
        moduleName={
          contactModal.moduleId === 'eNO1' ? 'eNO1 Sampling' :
          contactModal.moduleId === 'e55' ? 'e55 Weights' :
          contactModal.moduleId === 'e53' ? 'e53 Sampling' :
          contactModal.moduleId === 'e09' ? 'e09 IBRS' :
          ''
        }
      />
    </Box>
  );
};
